import { apiSlice } from './apiSlice';
import {
  AppointmentsData,
  IAdminAuhtorizedToReceiveCodeRequest,
  IAdminAuhtorizedToReceiveCodeResponse,
  querType,
  SendVerificationCodeInput,
  SendVerificationCodeResponse,
  TTotalRevenue,
} from '@/types/general';

export const usersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllTransactions: builder.query<AppointmentsData, querType>({
      query: (filters) => {
        const params = new URLSearchParams(filters).toString();
        return {
          url: `${import.meta.env.VITE_API_URL}/all-transactions?${params}`,
          method: 'GET',
        };
      },
    }),
    getAuth: builder.query<{ status: string; username: string; template: string }, {}>({
      query: () => {
        return {
          url: `${import.meta.env.VITE_API_URL}/islogged`,
          method: 'GET',
        };
      },
    }),
    getUserCanDelete: builder.query<{ status: string; username: string; id: number }, {}>({
      query: () => {
        return {
          url: `${import.meta.env.VITE_API_URL}/candelete`,
          method: 'GET',
        };
      },
    }),
    getMyUsers: builder.query<{ username: string; full_name: string }[], {}>({
      query: () => {
        return {
          url: `${import.meta.env.VITE_API_URL}/my-users`,
          method: 'GET',
        };
      },
    }),
    getLogs: builder.query<any, any>({
      query: (filters) => {
        const params = new URLSearchParams(filters).toString();
        return {
          url: `${import.meta.env.VITE_API_URL}/logs?${params}`,
          method: 'GET',
        };
      },
    }),
    refundTransaction: builder.mutation<any, any>({
      query: (data) => {
        return {
          url: `${import.meta.env.VITE_API_URL}/refund`,
          method: 'POST',
          body: data,
        };
      },
    }),
    sendVerificationCode: builder.mutation<SendVerificationCodeResponse, SendVerificationCodeInput>({
      query: (data) => {
        return {
          url: `${import.meta.env.VITE_API_URL}/send-verification-code`,
          method: 'POST',
          body: data,
        };
      },
    }),
    getAdminsAuthorizedToReceiveCode: builder.query<
      IAdminAuhtorizedToReceiveCodeResponse,
      IAdminAuhtorizedToReceiveCodeRequest
    >({
      query: () => {
        return {
          url: `${import.meta.env.VITE_API_URL}/admins-authorized-to-receive-code`,
          method: 'GET',
        };
      },
    }),
    deleteTransaction: builder.mutation<any, any>({
      query: (data) => {
        return {
          url: `${import.meta.env.VITE_API_URL}/delete-transaction`,
          method: 'POST',
          body: data,
        };
      },
    }),
    resetAttempts: builder.mutation<{ message: string }, Record<string, never>>({
      query: (data) => {
        return {
          url: `${import.meta.env.VITE_API_URL}/reset-attempts`,
          method: 'POST',
          body: data,
        };
      },
    }),
    addDisburseTransaction: builder.mutation<any, any>({
      query: (data) => {
        return {
          url: `${import.meta.env.VITE_API_URL}/disburse`,
          method: 'POST',
          body: data,
        };
      },
    }),

    regularizeTransaction: builder.mutation<any, any>({
      query: (data) => {
        return {
          url: `${import.meta.env.VITE_API_URL}/regularize`,
          method: 'POST',
          body: data,
        };
      },
    }),
    checkAvoirLimit: builder.mutation<{ limit: number }, { transactionGroupId: number }>({
      query: (data) => {
        return {
          url: `${import.meta.env.VITE_API_URL}/check-avoir-limit`,
          method: 'POST',
          body: data,
        };
      },
    }),

    getTransactionTotalByMethodPayment: builder.query<TTotalRevenue, querType>({
      query: (filters) => {
        const params = new URLSearchParams(filters).toString();
        return {
          url: `${import.meta.env.VITE_API_URL}/get-transaction-total-by-method-payment?${params}`,
          method: 'GET',
        };
      },
    }),
    getTransactionLogs: builder.query<
      { result: { action_type: string; createdAt: Date; User: { username: string } }[] },
      { transactionID: number }
    >({
      query: (data) => {
        return {
          url: `${import.meta.env.VITE_API_URL}/transaction-log`,
          method: 'POST',
          body: data,
        };
      },
    }),
  }),
});

export const {
  useGetAllTransactionsQuery,
  useDeleteTransactionMutation,
  useGetAuthQuery,
  useGetUserCanDeleteQuery,
  useGetLogsQuery,
  useGetMyUsersQuery,
  useRefundTransactionMutation,
  useAddDisburseTransactionMutation,
  useRegularizeTransactionMutation,
  useGetTransactionTotalByMethodPaymentQuery,
  useCheckAvoirLimitMutation,
  useSendVerificationCodeMutation,
  useGetTransactionLogsQuery,
  useResetAttemptsMutation,
  useGetAdminsAuthorizedToReceiveCodeQuery,
} = usersApiSlice;
